import { useEffect, useRef, useState } from "react";
import useSelectionTooltip from "../../hooks/useSelectionTooltip";
import useTextFormatting from "../../hooks/useTextFormatting";
import useImageHandler from "../../hooks/useImageHandler";
import useContextMenu from "../../hooks/useContextMenu";

export default function EditableContent({ value, onChange, patchNo }) {
  const editorRef = useRef(null);
  const {
    fontSize,
    fontFamily,
    activeCommands,
    sizeArray,
    fontFamilyArray,
    formatText,
    syncContent,
  } = useTextFormatting(editorRef, onChange);
  const { contextMenu } = useContextMenu(editorRef, formatText);
  const { selectionFontSize } = useSelectionTooltip(editorRef);
  const [pasteData, setPasteData] = useState(null);
  const [pasteTooltipPos, setPasteTooltipPos] = useState({ visible: false });
  const [listSymbol, setListSymbol] = useState("•");
  const [olType, setOlType] = useState("1");

  useImageHandler(editorRef, patchNo, onChange);

  useEffect(() => {
    if (editorRef.current && value) {
      editorRef.current.innerHTML = value;
    }
  }, []);

  const handlePaste = (e) => {
    e.preventDefault();
    const html = e.clipboardData.getData("text/html");
    const text = e.clipboardData.getData("text/plain");

    if (html && /<img[\s\S]*?>/i.test(html)) return;

    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    const range = selection.getRangeAt(0);

    let rect;
    if (selection.isCollapsed) {
      const tempSpan = document.createElement("span");
      tempSpan.textContent = "\u200b";
      range.insertNode(tempSpan);
      rect = tempSpan.getBoundingClientRect();

      selection.removeAllRanges();
      const newRange = document.createRange();
      newRange.setStartAfter(tempSpan);
      newRange.collapse(true);
      selection.addRange(newRange);
      tempSpan.remove();
    } else {
      rect = range.getBoundingClientRect();
    }

    setPasteData({ html, text, range });
    setPasteTooltipPos({
      visible: true,
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
  };

  const pasteWithFormat = () => {
    if (pasteData) {
      pasteData.range.deleteContents();
      pasteData.range.insertNode(document.createTextNode(""));
      pasteData.range.collapse(false);
      pasteData.range.startContainer.parentNode.focus();
      
      document.execCommand("insertHTML", false, pasteData.html);

      syncContent();
    }
    setPasteTooltipPos({ visible: false, top: 0, left: 0 });
  };

  const pasteAsPlain = () => {
    if (pasteData) {
      pasteData.range.deleteContents();
      pasteData.range.insertNode(document.createTextNode(""));
      pasteData.range.collapse(false);

      pasteData.range.startContainer.parentNode.focus();
      document.execCommand("insertText", false, pasteData.text);

      syncContent();
    }
    setPasteTooltipPos({ visible: false, top: 0, left: 0 });
  };

  return (
    <div className="flex flex-col">
      <label className="text-start font-bold text-gray-400 mt-4 mb-2">
        Bölüm İçeriği
      </label>

      <div className="flex space-x-2 mb-2 flex-wrap">
        <button
          type="button"
          onClick={() => formatText("bold")}
          className={`px-2 py-1 border rounded ${
            activeCommands.bold ? "bg-white text-black" : "bg-black text-white"
          }`}
        >
          B
        </button>
        <button
          type="button"
          onClick={() => formatText("italic")}
          className={`px-2 py-1 border rounded ${
            activeCommands.italic
              ? "bg-white text-black"
              : "bg-black text-white"
          }`}
        >
          I
        </button>
        <button
          type="button"
          onClick={() => formatText("underline")}
          className={`px-2 py-1 border rounded ${
            activeCommands.underline
              ? "bg-white text-black"
              : "bg-black text-white"
          }`}
        >
          U
        </button>
        <select
          onChange={(e) => formatText("fontSize", e.target.value)}
          value={fontSize}
          className="border flex rounded px-2 py-1 bg-black text-white"
        >
          {sizeArray.map((i) => (
            <option key={i} value={`${i}px`}>
              {i}px
            </option>
          ))}
        </select>

        <select
          onChange={(e) => formatText("fontFamily", e.target.value)}
          value={fontFamily}
          className="border rounded px-2 py-1 bg-black text-white"
        >
          {fontFamilyArray.map((i) => (
            <option key={i} value={i}>
              {i}
            </option>
          ))}
        </select>
        <select
          onChange={(e) => {
            if (e.target.value === "nolist")
              return formatText("insertUnorderedList", null), setListSymbol("");
            formatText("insertUnorderedList", null, e.target.value);
            setListSymbol(e.target.value);
          }}
          className="px-2 py-1 border rounded text-white bg-gray-800"
        >
          {["nolist", "●", "■", "★", "✦", "→", "–", "▲", "▼", "▶"].map(
            (symbol) => (
              <option
                className={listSymbol === symbol ? "bg-gray-500" : ""}
                key={symbol}
                value={symbol}
              >
                {symbol}
              </option>
            )
          )}
        </select>
        <select
          onChange={(e) => {
            if (e.target.value === "nolist")
              return (
                formatText("insertOrderedList", null, olType), setOlType("")
              );
            formatText("insertOrderedList", null, e.target.value);
            setOlType(e.target.value);
          }}
          className="px-2 py-1 border rounded bg-gray-800 text-white"
        >
          {[
            { label: "nolist", value: null },
            { label: "1, 2, 3...", value: "decimal" },
            { label: "A, B, C...", value: "upper-alpha" },
            { label: "a, b, c...", value: "lower-alpha" },
            { label: "I, II, III...", value: "upper-roman" },
            { label: "i, ii, iii...", value: "lower-roman" },
          ].map((type) => (
            <option
              className={olType === type.value ? "bg-gray-500" : ""}
              key={type.value}
              value={type.value}
            >
              {type.label}
            </option>
          ))}
        </select>
      </div>

      {pasteTooltipPos.visible && (
        <div
          className="absolute bg-black text-white p-1 rounded-sm z-50"
          style={{
            top: pasteTooltipPos.top,
            left: pasteTooltipPos.left,
          }}
        >
          {fontFamily}:{" " + selectionFontSize}
        </div>
      )}

      {contextMenu.visible && (
        <div
          className="absolute bg-black text-white p-1 space-x-2 rounded-sm z-50"
          style={{ top: contextMenu.top, left: contextMenu.left }}
          onClick={(e) => e.stopPropagation()}
        >
          <div>
            <select
              onChange={(e) => formatText("fontSize", e.target.value)}
              value={fontSize}
              className="border rounded px-2 py-1 bg-black text-white mb-1"
            >
              {sizeArray.map((i) => (
                <option key={i} value={`${i}px`}>
                  {i}px
                </option>
              ))}
            </select>
            <select
              onChange={(e) => formatText("fontFamily", e.target.value)}
              value={fontFamily}
              className="border rounded px-2 py-1 bg-black text-white"
            >
              {fontFamilyArray.map((i) => (
                <option key={i} value={i}>
                  {i}
                </option>
              ))}
            </select>
            <select
              onChange={(e) => {
                if (e.target.value === "nolist")
                  return (
                    formatText("insertUnorderedList", null), setListSymbol("")
                  );
                formatText("insertUnorderedList", null, e.target.value);
                setListSymbol(e.target.value);
              }}
              className="px-2 py-1 border rounded text-white bg-gray-800"
            >
              {["nolist", "●", "■", "★", "✦", "→", "–", "▲", "▼", "▶"].map(
                (symbol) => (
                  <option
                    className={listSymbol === symbol ? "bg-gray-500" : ""}
                    key={symbol}
                    value={symbol}
                  >
                    {symbol}
                  </option>
                )
              )}
            </select>
            <select
              onChange={(e) => {
                if (e.target.value === "nolist")
                  return (
                    formatText("insertOrderedList", null, olType), setOlType("")
                  );
                formatText("insertOrderedList", null, e.target.value);
                setOlType(e.target.value);
              }}
              className="px-2 py-1 border rounded bg-gray-800 text-white"
            >
              {[
                { label: "nolist", value: null },
                { label: "1, 2, 3...", value: "decimal" },
                { label: "A, B, C...", value: "upper-alpha" },
                { label: "a, b, c...", value: "lower-alpha" },
                { label: "I, II, III...", value: "upper-roman" },
                { label: "i, ii, iii...", value: "lower-roman" },
              ].map((type) => (
                <option
                  className={olType === type.value ? "bg-gray-500" : ""}
                  key={type.value}
                  value={type.value}
                >
                  {type.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
      {pasteTooltipPos.visible && (
        <div
          className="absolute bg-gray-800 text-white text-sm p-2 rounded shadow-lg z-50 flex space-x-2"
          style={{ top: pasteTooltipPos.top, left: pasteTooltipPos.left }}
        >
          <button
            className="bg-green-600 text-white px-2 py-1 rounded"
            onClick={pasteWithFormat}
          >
            Formatı Koru
          </button>
          <button
            className="bg-blue-600 text-white px-2 py-1 rounded"
            onClick={pasteAsPlain}
          >
            Sadece Metni Yapıştır
          </button>
        </div>
      )}

      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={syncContent}
        onPaste={handlePaste}
        className="border text-start text-white border-gray-300 rounded p-2 px-4 min-h-[180px] focus:outline-none focus:ring-2 focus:ring-amber-400"
        style={{ whiteSpace: "discard-inner", color: "white" }}
        dangerouslySetInnerHTML={{ __html: value.replace(/background(-color)?:\s*[^;"]+/gi, 'background-color: transparent'), }}
      />

      {/* Sadece update kısmında resim kenarları gösterileceği için burada */}
      <style>{`
        .img-box { display:inline-flex; position:relative; margin:2px; outline:2px solid transparent; vertical-align:middle;}
        .img-box.selected { outline-color:#4dabf7; }
        .img-box .img-handle { position:absolute; width:10px; height:10px; background:#4dabf7; border:1px solid white; box-sizing:border-box; display:block; opacity:20; transition:opacity .12s; }
        .img-box:hover .img-handle, .img-box.selected .img-handle { opacity:1; }
        .img-box .img-handle.nw { left:-5px; top:-5px; cursor:nwse-resize; }
        .img-box .img-handle.ne { right:-5px; top:-5px; cursor:nesw-resize; }
        .img-box .img-handle.sw { left:-5px; bottom:-5px; cursor:nesw-resize; }
        .img-box .img-handle.se { right:-5px; bottom:-5px; cursor:nwse-resize; }
        .img-box img { display:block; max-width:100%; height:100%; }
      `}</style>
    </div>
  );
}
